import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Navbar from './components/layout/Navbar';
import { themes } from './components/ThemeSelector';
import { selectTheme } from './store/themeSlice';
import { fetchAllPokemon } from './store/pokemonSlice';
import { fetchCurrentUser, selectAccessToken, selectIsAuthenticated } from './store/authSlice';
import { fetchUserData } from './store/userDataSlice';
import SocialSupport from './components/SocialSupport';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './pages/Login';
import Signup from './pages/Signup';
import UserProfile from './pages/UserProfile';

import Home from './pages/Home';
import PokemonDatabase from './pages/PokemonDatabase';
import PokemonDetail from './pages/PokemonDetail';
import Competitive from './pages/Competitive';
import TypeChart from './pages/TypeChart';
import Abilities from './pages/Abilities';
import AbilityDetail from './pages/AbilityDetail';
import Moves from './pages/Moves';
import MoveDetail from './pages/MoveDetail';
import Items from './pages/Items';
import ItemDetail from './pages/ItemDetail';
import Quizzes from './pages/Quizzes';
import QuickCalc from './pages/QuickCalc';
import CalcTestRunner from './pages/CalcTestRunner';
import './App.css';

function App() {
  const dispatch = useDispatch();
  const currentTheme = useSelector(selectTheme);
  const location = useLocation();
  const accessToken = useSelector(selectAccessToken);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  useEffect(() => {
    // Reset scroll position on route change
    window.scrollTo(0, 0);
  }, [location.pathname, location.key]);

  useEffect(() => {
    // Fetch Pokemon data when app starts
    dispatch(fetchAllPokemon());
  }, [dispatch]);

  useEffect(() => {
    // Restore session from stored token on app load
    if (accessToken) {
      dispatch(fetchCurrentUser());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Load favorites and shinies whenever the user is authenticated
    if (isAuthenticated) {
      dispatch(fetchUserData());
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    // Apply theme colors to CSS variables
    const theme = themes[currentTheme].colors;
    document.documentElement.style.setProperty('--primary-color', theme.primary);
    document.documentElement.style.setProperty('--secondary-color', theme.secondary);
    document.documentElement.style.setProperty('--accent-color', theme.accent);
  }, [currentTheme]);

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
      <div className="App">
        <Navbar currentTheme={currentTheme} />
        <main className="container">
          <div key={location.pathname} className="page-enter">
            <Routes>
              <Route path="/" element={<Home currentTheme={currentTheme} />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/profile" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
              <Route path="/database" element={<PokemonDatabase />} />
              <Route path="/pokemon/:id" element={<PokemonDetail />} />
              <Route path="/competitive/*" element={<Competitive />} />
              <Route path="/abilities" element={<Abilities />} />
              <Route path="/abilities/:name" element={<AbilityDetail />} />
              <Route path="/moves" element={<Moves />} />
              <Route path="/moves/:name" element={<MoveDetail />} />
              <Route path="/items" element={<Items />} />
              <Route path="/items/:name" element={<ItemDetail />} />
              <Route path="/types" element={<TypeChart />} />
              <Route path="/quizzes/*" element={<Quizzes />} />
              <Route path="/quickcalc" element={<QuickCalc />} />
              <Route path="/calc-test" element={<CalcTestRunner />} />
            </Routes>
          </div>
        </main>
        <SocialSupport />
      </div>
    </GoogleOAuthProvider>
  );
}

export default App; 